import api from "../api";

const ENDPOINTS = {
  warehouses: "/products/warehouses-address/",
  warehouseTable: "/products/inventory/warehouse-table/",
  productTable: "/products/inventory/product_table/",
  upsert: "/products/inventory/upsert/",
  bulkUpdate: "/products/inventory/bulk-update/",
  export: "/products/inventory/export/",
};

const normalizeWarehouseId = (warehouseId) => {
  if (warehouseId && typeof warehouseId === "object") {
    return warehouseId.id ?? "";
  }
  return warehouseId;
};
const toGregorianISO = (date) => {
    if (!date) return '';
    return date.toDate().toISOString();
  };

const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

const buildInventoryParams = ({
  warehouseId,
  search = "",
  page,
  pageSize,
  noPagination = false,
  ordering,
  isActive,
  dateFrom,
  dateTo,
} = {}) => {
  const params = {};
  const normalizedWarehouseId = normalizeWarehouseId(warehouseId);

  if (
    normalizedWarehouseId !== undefined &&
    normalizedWarehouseId !== null &&
    normalizedWarehouseId !== ""
  ) {
    params.warehouse = normalizedWarehouseId;
  }

  if (search && String(search).trim()) {
    params.search = String(search);
  }

  if (ordering && String(ordering).trim()) {
    params.ordering = String(ordering).trim();
  }

  if (isActive !== undefined && isActive !== null && isActive !== "") {
    params.is_active = isActive;
  }

  if (dateFrom) {
    params.date_from = toGregorianISO(dateFrom);
  }

  if (dateTo) {
    params.date_to = toGregorianISO(dateTo);
  }

  if (noPagination) {
    params.no_pagination = "true";
  } else {
    if (page) {
      params.page = page;
    }
    if (pageSize) {
      params.page_size = pageSize;
    }
  }

  return params;
};

const addDirtyStateToProducts = (products = []) => {
  return products.map((product) => ({
    ...product,
    isDirty: false,
    originalData: {
      price: product.price ?? "",
      stock: product.stock ?? "",
      discount_percent: product.discount_percent ?? "",
      min_order: product.min_order ?? "",
      max_order: product.max_order ?? "",
      order_multiple: product.order_multiple ?? "",
      is_active: product.is_active ?? true,
    },
  }));
};

const addDirtyStateToWarehouseProducts = (products = []) => {
  return products.map((product) => ({
    ...product,
    isDirty: false,
    originalData: {
      price: product.price ?? "",
      stock: product.stock ?? "",
      discount_percent: product.discount_percent ?? "",
      min_order: product.min_order ?? "",
      max_order: product.max_order ?? "",
      order_multiple: product.order_multiple ?? "",
      is_active: product.is_active ?? true,
    },
  }));
};

const InventoryService = {
  async getWarehouses() {
    const { data } = await api.get(ENDPOINTS.warehouses);
    return data;
  },

  async getWarehouseProducts({
    warehouseId,
    search = "",
    page = 1,
    pageSize = 10,
    noPagination = false,
    withDirtyState = true,
    ordering,
    isActive,
    dateFrom,
    dateTo,
  } = {}) {
    const params = buildInventoryParams({
      warehouseId,
      search,
      page,
      pageSize,
      noPagination,
      ordering,
      isActive,
      dateFrom,
      dateTo,
    });

    const { data } = await api.get(ENDPOINTS.warehouseTable, { params });

    if (noPagination) {
      return withDirtyState ? addDirtyStateToWarehouseProducts(data) : data;
    }

    return {
      ...data,
      results: withDirtyState
        ? addDirtyStateToWarehouseProducts(data.results || [])
        : data.results || [],
    };
  },

  async saveProductInventory({
    productId,
    warehouseId,
    price,
    stock,
    discount_percent,
    min_order,
    max_order,
    order_multiple,
    is_active,
  }) {
    const payload = {
      product_id: productId,
      warehouse_id: normalizeWarehouseId(warehouseId),
      price: price === "" ? null : price,
      stock: stock === "" ? null : stock,
      discount_percent: discount_percent === "" ? null : discount_percent,
      min_order: min_order === "" ? null : min_order,
      max_order: max_order === "" ? null : max_order,
      order_multiple: order_multiple === "" ? null : order_multiple,
      is_active: is_active,
    };

    const { data } = await api.post(ENDPOINTS.upsert, payload);
    return data;
  },

  async bulkUpdateInventory(payload ) {
    const { data } = await api.post(ENDPOINTS.bulkUpdate, payload);
    return data;
  },

  async exportExcel({
    warehouseId,
    search = "",
    ordering,
    isActive,
    dateFrom,
    dateTo,
  } = {}) {
    const params = buildInventoryParams({
      warehouseId,
      search,
      noPagination: true,
      ordering,
      isActive,
      dateFrom,
      dateTo,
    });

    const { data } = await api.get(ENDPOINTS.export, { params });
    return data;
  },
};

export default InventoryService;
